( function ( $ ) {
	/**
	 * SELECTORS is an object containing CSS selector strings used to select elements on the page.
	 * These selectors correspond to various inputs and elements in the ticket booking form.
	 */
	const SELECTORS = {
		departingStation: '#input_3_1', // Selector for the departing station input
		arrivingStation: '#input_3_3', // Selector for the arriving station input
		departingTime: '#input_3_4', // Selector for the departing time input
		arrivingTime: '#input_3_5', // Selector for the arriving time input
		ticketType: '#input_3_9', // Selector for the ticket type input
		timeDifference: '#input_3_8', // Selector for the time difference display input
		ticketCost: '#input_3_10', // Selector for the ticket cost display input
	};

	/**
	 * DEPARTING_WRAPPER_NUM and ARRIVING_WRAPPER_NUM are constants used to identify the specific
	 * wrappers or segments of the UI that correspond to departing and arriving times.
	 */
	const DEPARTING_WRAPPER_NUM = '4'; // Identifier for the departing time wrapper
	const ARRIVING_WRAPPER_NUM = '5'; // Identifier for the arriving time wrapper

	/**
	 * FALSY_VALUES is an array containing values that are considered falsy in the context of the application,
	 * which includes various representations of a false or unset value.
	 */
	const FALSY_VALUES = [ 0, '0', '', null, false, 'false' ];

	/**
	 * NORTH_BOUND and SOUTH_BOUND are constants representing the two possible directions or "bounds" of travel.
	 */
	const NORTH_BOUND = 'nb'; // Constant representing northbound direction
	const SOUTH_BOUND = 'sb'; // Constant representing southbound direction

	/**
	 * Trip Planner class for managing train station selections.
	 */
	class TripPlanner {
		/**
		 * Constructor for the TripPlanner class.
		 */
		constructor() {
			this.cacheDom();
			this.getEventMapping = this.getEventMapping.bind( this );
			this.attachEventListeners = this.attachEventListeners.bind( this );
			this.handleChange = this.handleChange.bind( this );
			this.handleTimeChange = this.handleTimeChange.bind( this );
			this.updateFields = this.updateFields.bind( this );
			this.setBound = this.setBound.bind( this );
			this.populateTimes = this.populateTimes.bind( this );
			this.populateSelectField = this.populateSelectField.bind( this );
			this.populateTimeField = this.populateTimeField.bind( this );
			this.calculateTimeDifference = this.calculateTimeDifference.bind( this );
			this.calculateTicketCost = this.calculateTicketCost.bind( this );
			this.getTimesForStation = this.getTimesForStation.bind( this );
			this.init();
		}

		/**
		 * Cache DOM elements for better performance.
		 */
		cacheDom = () => {
			this.$departingStation = $( SELECTORS.departingStation );
			this.$arrivingStation = $( SELECTORS.arrivingStation );
			this.$departingTime = $( SELECTORS.departingTime );
			this.$arrivingTime = $( SELECTORS.arrivingTime );
			this.$ticketType = $( SELECTORS.ticketType );
			this.$timeDifference = $( SELECTORS.timeDifference );
			this.$ticketCost = $( SELECTORS.ticketCost );
		};

		/**
		 * Initializes the trip planner.
		 */
		init() {
			this.setInitialValues();
			this.attachEventListeners();
		}

		/**
		 * Set initial values for the trip planner.
		 */
		setInitialValues() {
			this.departingStationId = '0';
			this.arrivingStationId = '0';
			this.bound = false;
			this.departingTime = false;
			this.arrivingTime = false;
			this.initialTimeDifference = this.$timeDifference.val();
		}

		/**
		 * Generates the event mapping for attaching event listeners.
		 */
		getEventMapping() {
			const { $ticketType, $arrivingTime, $departingStation, $departingTime, $arrivingStation } = this;
			return [
				{ element: $departingStation, handler: 'handleChange', arg: 'departing' },
				{ element: $arrivingStation, handler: 'handleChange', arg: 'arriving' },
				{ element: $departingTime, handler: 'handleTimeChange', arg: 'departing' },
				{ element: $arrivingTime, handler: 'handleTimeChange', arg: 'arriving' },
				{ element: $departingTime, handler: 'calculateTimeDifference' },
				{ element: $arrivingTime, handler: 'calculateTimeDifference' },
				{ element: $ticketType, handler: 'calculateTicketCost' },
			];
		}

		/**
		 * Attaches change event listeners to the relevant input fields.
		 */
		attachEventListeners() {
			const { getEventMapping } = this;
			getEventMapping().forEach( ( { element, handler, arg } ) => {
				const boundHandler = arg ? this[ handler ].bind( this, arg ) : this[ handler ].bind( this );
				if ( typeof boundHandler === 'function' ) {
					element.on( 'change', boundHandler );
				} else {
					console.error( `Handler function ${ handler } is not defined` ); // eslint-disable-line no-console
				}
			} );
		}

		/**
		 * Handles change event for the station select fields.
		 *
		 * @param {string} stationType - The type of station, either 'departing' or 'arriving'.
		 */
		handleChange( stationType ) {
			this[ `${ stationType }StationId` ] = this[ `$${ stationType }Station` ].val();
			this.updateFields();
			this.setBound();
			this.populateTimes();
			this.calculateTicketCost();
		}

		/**
		 * Handles change event for the time select fields.
		 *
		 * @param {string} timeType - The type of time, either 'departing' or 'arriving'.
		 */
		handleTimeChange( timeType ) {
			const selectedTime = this[ `$${ timeType }Time` ].find( 'option:selected' ).val();
			if ( selectedTime ) {
				this[ `${ timeType }Time` ] = selectedTime;
				if ( timeType === 'departing' ) {
					this.updateDepartingTimeField();
				} else {
					this.updateArrivingTimeField();
				}
			}
		}

		/**
		 * Updates the departing and arriving select fields to disable the selected option in the other field.
		 */
		updateFields() {
			const { $departingStation, $arrivingStation } = this;
			const departingStationId = $departingStation.val();
			const arrivingStationId = $arrivingStation.val();

			$departingStation
				.find( 'option' )
				.prop( 'disabled', false )
				.filter( ( _, option ) => $( option ).val() === arrivingStationId )
				.prop( 'disabled', true );
			$arrivingStation
				.find( 'option' )
				.prop( 'disabled', false )
				.filter( ( _, option ) => $( option ).val() === departingStationId )
				.prop( 'disabled', true );
		}

		/**
		 * Sets the train direction bound based on the selected stations.
		 */
		setBound() {
			const { $departingStation, $arrivingStation } = this;
			const departingStationId = $departingStation.val();
			const arrivingStationId = $arrivingStation.val();

			if ( FALSY_VALUES.includes( departingStationId ) || FALSY_VALUES.includes( arrivingStationId ) ) {
				this.bound = false;
				return;
			}

			if ( parseInt( $departingStation.val() ) < 0 || parseInt( $arrivingStation.val() ) < 0 ) {
				this.bound = false;
				return;
			}

			const rawScheduleDataKeys = Object.keys( window.scheduleData );

			const scheduleDataKeys = rawScheduleDataKeys.sort( ( a, b ) => {
				const orderA = window.scheduleData[ a ]?.order;
				const orderB = window.scheduleData[ b ]?.order;

				if ( orderA === null && orderB === null ) {
					return 0;
				} else if ( orderA === undefined ) {
					return 1; // Place items without 'order' at the end
				} else if ( orderB === undefined ) {
					return -1; // Place items without 'order' at the end
				}
				return orderA - orderB;
			} );

			const departingIndex = scheduleDataKeys.indexOf( departingStationId );
			const arrivingIndex = scheduleDataKeys.indexOf( arrivingStationId );

			this.bound = departingIndex > arrivingIndex ? NORTH_BOUND : SOUTH_BOUND;
		}

		/**
		 * Resets the times and UI elements to their default states.
		 */
		resetTimes() {
			this.populateTimeField( DEPARTING_WRAPPER_NUM, '0' );
			this.populateTimeField( ARRIVING_WRAPPER_NUM, '0' );
			window.scheduleData.earliestDepartingTime = false;
			window.scheduleData.latestArrivingTime = false;
			window.scheduleData.indexDiff = false;
		}

		/**
		 * Retrieves the times array for a given station.
		 *
		 * @param {string} stationId - The ID of the station.
		 * @return {Array<string>} An array of time strings.
		 */
		getTimesForStation( stationId ) {
			const stationIdInt = parseInt( stationId, 10 );
			if ( isNaN( stationIdInt ) ) {
				console.error( 'Station ID is not a valid number:', stationId ); // eslint-disable-line no-console
				return [];
			}
			return Object.values( window.scheduleData[ stationIdInt ].times[ this.bound ] ).flatMap( ( timeOfDay ) =>
				Object.values( timeOfDay )
			);
		}

		/**
		 * Populates the times for departing and arriving stations.
		 */
		populateTimes() {
			const { bound, departingStationId, arrivingStationId, populateTimeField } = this;

			if ( ! bound ) {
				this.resetTimes();
				return;
			}

			const departingTimes = this.getTimesForStation( departingStationId );
			const arrivingTimes = this.getTimesForStation( arrivingStationId );
			const lastArrivingTime = this.findValidTime( arrivingTimes, 'last' );

			this.departingTime = this.findValidTime( departingTimes );
			this.arrivingTime = this.findValidTime( arrivingTimes );

			// Preset earliest departing and latest arriving times
			window.scheduleData.earliestDepartingTime = departingTimes.length
				? this.convertTimeToDate( this.departingTime )
				: null;
			window.scheduleData.latestArrivingTime = arrivingTimes.length
				? this.convertTimeToDate( lastArrivingTime )
				: null;

			// Preset indexes for the currently set departing and arriving times
			const departingTimeIndex = departingTimes.findIndex( ( time ) => time === this.departingTime );
			const arrivingTimeIndex = arrivingTimes.findIndex( ( time ) => time === this.arrivingTime );

			window.scheduleData.indexDiff = departingTimeIndex - arrivingTimeIndex;

			populateTimeField(
				DEPARTING_WRAPPER_NUM,
				departingStationId,
				'departing',
				departingTimes,
				departingTimeIndex,
				arrivingTimeIndex
			);
			populateTimeField(
				ARRIVING_WRAPPER_NUM,
				arrivingStationId,
				'arriving',
				arrivingTimes,
				departingTimeIndex,
				arrivingTimeIndex
			);
		}

		/**
		 * Populates the time field for a given direction with the provided times.
		 *
		 * @param {number}        wrapperNum         - The wrapper number for the time field.
		 * @param {string}        stationId          - The station ID for which to populate times.
		 * @param {string}        direction          - The direction, either 'departing' or 'arriving'.
		 * @param {Array<string>} times              - An array of time strings to populate the field.
		 * @param {number}        departingTimeIndex - The index of the selected departing time.
		 * @param {number}        arrivingTimeIndex  - The index of the selected arriving time.
		 */
		populateTimeField( wrapperNum, stationId, direction, times, departingTimeIndex, arrivingTimeIndex ) {
			const { $arrivingTime, populateSelectField: populateSelectField1, $departingTime } = this;
			const selectField = direction === 'departing' ? $departingTime : $arrivingTime;

			if ( FALSY_VALUES.includes( stationId ) ) {
				selectField.empty().append( $( '<option>' ).text( '00:00' ) ).prop( 'disabled', true );
				return;
			}

			selectField.empty().prop( 'disabled', false );

			populateSelectField1( selectField, direction, times, departingTimeIndex, arrivingTimeIndex );
		}

		/**
		 * Populates the given select field with options based on the provided times and indexes.
		 *
		 * @param {jQuery}        selectField        - The jQuery object representing the select field to populate.
		 * @param {string}        direction          - The direction, either 'departing' or 'arriving'.
		 * @param {Array<string>} times              - An array of time strings to use for options.
		 * @param {number}        departingTimeIndex - The index of the selected departing time.
		 */
		populateSelectField( selectField, direction, times, departingTimeIndex ) {
			times.forEach( ( timeStr, index ) => {
				if ( timeStr ) {
					const option = $( '<option>' ).val( timeStr ).text( timeStr );
					let isDisabled = false;
					const time = this.convertTimeToDate( timeStr );

					if ( direction === 'departing' && time >= window.scheduleData.latestArrivingTime ) {
						isDisabled = true;
					} else if (
						direction === 'arriving' &&
						( index < departingTimeIndex || time <= window.scheduleData.earliestDepartingTime )
					) {
						isDisabled = true;
					}

					option.prop( 'disabled', isDisabled );

					if ( ! isDisabled && ! selectField.val() ) {
						if ( direction === 'arriving' && index === departingTimeIndex ) {
							selectField.val( timeStr );
							this[ `${ direction }Time` ] = timeStr;
						} else if ( direction === 'departing' ) {
							selectField.val( timeStr );
							this[ `${ direction }Time` ] = timeStr;
						}
					}

					selectField.append( option );
				}
			} );

			if ( this.departingTime && this.arrivingTime ) {
				this.calculateTimeDifference();
			}
		}

		/**
		 * Updates the arriving time field based on the departing time.
		 */
		updateArrivingTimeField() {
			this.updateTimeField( 'arriving', this.arrivingTime );
		}

		/**
		 * Updates the departing time field based on the arriving time.
		 */
		updateDepartingTimeField() {
			this.updateTimeField( 'departing', this.departingTime );
		}

		/**
		 * Updates a time select field based on another time.
		 *
		 * @param {string} changedTimeType - The type of time being updated, either 'departing' or 'arriving'.
		 * @param {string} newTimeValue    - The time to compare against, in "hh:mm am/pm" format.
		 */
		updateTimeField( changedTimeType, newTimeValue ) {
			const {
				formatDateToTimeStr,
				convertTimeToDate,
				getTimesForStation,
				departingStationId,
				arrivingStationId,
				$departingTime,
				$arrivingTime,
			} = this;

			// Set the current and other time fields based on changedTimeType
			const currentTimeField = changedTimeType === 'departing' ? $departingTime : $arrivingTime;
			const otherTimeField = changedTimeType === 'arriving' ? $departingTime : $arrivingTime;
			const currentStationId = changedTimeType === 'departing' ? departingStationId : arrivingStationId;
			const otherStationId = changedTimeType === 'arriving' ? departingStationId : arrivingStationId;

			// Convert the new time value to a Date object and validate it
			const newTimeDate = convertTimeToDate( newTimeValue );
			if ( ! newTimeDate ) {
				console.error( 'Invalid new time value:', newTimeValue ); // eslint-disable-line no-console
				return;
			}

			// Update the time property and value for the current time field
			this[ `${ changedTimeType }Time` ] = newTimeValue;
			currentTimeField.val( newTimeValue );

			// Get the list of times for the current and other stations
			const currentStationTimes = getTimesForStation( currentStationId );
			const otherStationTimes = getTimesForStation( otherStationId );

			// Find the index of the new time in the current station times array
			const newTimeIndex = currentStationTimes.findIndex( ( time ) => time === newTimeValue );

			// Find a time in the other station times that matches the index and comparison requirements
			const otherTimeToUpdate = otherStationTimes[ newTimeIndex ];
			const otherTimeToUpdateDate = convertTimeToDate( otherTimeToUpdate );

			// Check if the found time is valid based on the changed time type and update accordingly
			if (
				otherTimeToUpdate &&
				( ( changedTimeType === 'departing' && otherTimeToUpdateDate > newTimeDate ) ||
					( changedTimeType === 'arriving' && otherTimeToUpdateDate < newTimeDate ) )
			) {
				const formattedOtherTime = formatDateToTimeStr( otherTimeToUpdateDate );
				otherTimeField.val( formattedOtherTime );
				this[ `${ changedTimeType === 'departing' ? 'arriving' : 'departing' }Time` ] = formattedOtherTime;
			} else {
				// If no valid other time is found, log an error
				console.error( `Cannot find a valid ${ otherTimeToUpdate } time that maintains the index difference.` ); // eslint-disable-line no-console
			}
		}

		/**
		 * Finds either the first or last valid time string from the provided array of times.
		 *
		 * @param {Array<string>} times - An array of time strings.
		 * @param {string}        which - Specifies whether to get the 'first' or 'last' valid time. Defaults to 'first'.
		 * @return {string|null} The first or last valid time string, or null if none are valid.
		 */
		findValidTime( times, which = 'first' ) {
			// Helper function to determine the validity of a time string.
			const isValidTime = ( time ) => this.isValidTime( time );

			// Helper functions to get the first or the last valid time.
			const getFirstValidTime = () => times.find( isValidTime ) || null;
			const getLastValidTime = () => {
				for ( let i = times.length - 1; i >= 0; i-- ) {
					if ( isValidTime( times[ i ] ) ) {
						return times[ i ];
					}
				}
				return null;
			};

			// Return the appropriate valid time based on 'which' parameter.
			return which === 'first' ? getFirstValidTime() : getLastValidTime();
		}

		/**
		 * Converts a time string to a JavaScript Date object.
		 *
		 * The input time string should be in the format "hh:mm am/pm",
		 * such as "12:30 pm" or "02:15 am". This method converts the
		 * input time string into a JavaScript Date object set to that
		 * specific time on January 1, 1970. This allows for easy time
		 * comparison and manipulation.
		 *
		 * @param {string} timeStr - The time string to convert, formatted as "hh:mm am/pm".
		 * @return {Date} A JavaScript Date object representing the specified time.
		 */
		convertTimeToDate( timeStr ) {
			if ( ! timeStr || typeof timeStr !== 'string' ) return null;

			const [ time, period ] = timeStr.split( ' ' );
			if ( ! time || ! period ) return null;

			let [ hours, minutes ] = time.split( ':' ).map( Number );
			if ( isNaN( hours ) || isNaN( minutes ) || hours < 0 || hours > 12 || minutes < 0 || minutes >= 60 )
				return null;

			if ( period.toLowerCase() === 'pm' && hours !== 12 ) {
				hours += 12;
			} else if ( period.toLowerCase() === 'am' && hours === 12 ) {
				hours = 0;
			}

			const now = new Date();
			return new Date( now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes );
		}

		/**
		 * Formats a JavaScript Date object to a time string.
		 *
		 * This method converts a JavaScript Date object into a formatted time string in
		 * the format of "hh:mm am/pm". For example, an input Date object representing
		 * 3:45 PM would be converted to the string "3:45 pm".
		 *
		 * @param {Date} date - A JavaScript Date object representing the time to format.
		 * @return {string} The formatted time string.
		 */
		formatDateToTimeStr( date ) {
			if ( ! ( date instanceof Date ) ) return '';

			let hours = date.getHours();
			const minutes = date.getMinutes();
			const ampm = hours >= 12 ? 'pm' : 'am';

			hours = hours % 12 || 12; // Convert 0 to 12 for 12-hour format
			const minutesStr = minutes < 10 ? `0${ minutes }` : `${ minutes }`;

			return `${ hours }:${ minutesStr } ${ ampm }`;
		}

		/**
		 * Validates whether a given string is a time in the format of "H:MM am/pm" or "HH:MM am/pm".
		 *
		 * @param {string} timeStr - The time string to validate.
		 * @return {boolean} True if the string is a valid time, false otherwise.
		 */
		isValidTime( timeStr ) {
			const pattern = /^(\d{1,2}):([0-5]\d) (am|pm)$/;
			return pattern.test( timeStr );
		}

		/**
		 * Calculates the time difference between the Arriving Time and Departure Time
		 * and updates the value of field 8.
		 */
		calculateTimeDifference() {
			const { arrivingTime, departingTime, $timeDifference, convertTimeToDate: convertTimeToDate1 } = this;
			const departingTimeDate = convertTimeToDate1( departingTime );
			const arrivingTimeDate = convertTimeToDate1( arrivingTime );

			if ( ! departingTimeDate || ! arrivingTimeDate ) {
				console.error( 'Invalid time values for calculating time difference:', departingTime, arrivingTime ); // eslint-disable-line no-console
				return;
			}

			const timeDifference = arrivingTimeDate - departingTimeDate;
			const differenceInMinutes = Math.abs( timeDifference / ( 1000 * 60 ) );

			const hours = Math.floor( differenceInMinutes / 60 );
			const minutes = differenceInMinutes % 60;
			const formattedDifference = hours > 0 ? `${ hours }h ${ minutes }min` : `${ minutes }min`;

			// Assuming you have a cached DOM element for this field
			$timeDifference.val( formattedDifference );

			if ( this.initialTimeDifference !== formattedDifference ) {
				$timeDifference.css( 'opacity', '1' );
			} else {
				$timeDifference.css( 'opacity', null );
			}
		}

		/**
		 * Calculates and updates the ticket cost based on selected options.
		 * Ensures data is valid and secure before proceeding with calculations.
		 */
		calculateTicketCost() {
			const {
				$ticketType,
				getDataAttributeAsInt: getDataAttributeAsInt1,
				$departingStation,
				formatCurrency: formatCurrency1,
				$ticketCost,
				$arrivingStation,
				arrivingTime,
				departingTime,
			} = this;

			if ( FALSY_VALUES.includes( departingTime ) || FALSY_VALUES.includes( arrivingTime ) ) {
				return;
			}

			const departingCounties = getDataAttributeAsInt1( $departingStation, 'counties' );
			const arrivingCounties = getDataAttributeAsInt1( $arrivingStation, 'counties' );

			if ( isNaN( departingCounties ) || isNaN( arrivingCounties ) ) {
				console.error( 'Invalid counties data for departing or arriving station.' ); // eslint-disable-line no-console
				$ticketCost.val( 'N/A' ); // or any other default/error value
				return;
			}

			const ticketType = $ticketType.val();

			if ( ! ticketType ) {
				console.error( 'Ticket type not selected.' ); // eslint-disable-line no-console
				$ticketCost.val( 'N/A' ); // or any other default/error value
				return;
			}

			const countiesIndex = Math.abs( departingCounties - arrivingCounties );
			const ticketCost = window.ticketPrices?.oneway?.[ ticketType ]?.[ countiesIndex ];
			if ( ticketCost !== undefined ) {
				const formattedCurrency = formatCurrency1( ticketCost );
				$ticketCost.val( formattedCurrency );
				if ( ticketCost !== 0 ) {
					$ticketCost.css( 'opacity', '1' );
				} else {
					$ticketCost.css( 'opacity', null );
				}
			} else {
				console.error( 'Ticket cost not found for selected options.' ); // eslint-disable-line no-console
				$ticketCost.val( 'N/A' ); // or any other default/error value
			}
		}

		/**
		 * Gets a data attribute from the selected option of a jQuery select element and parses it as an integer.
		 * @param {jQuery} $element  - The jQuery select element.
		 * @param {string} attribute - The data attribute to retrieve.
		 * @return {number} - The parsed integer value, or NaN if parsing fails.
		 */
		getDataAttributeAsInt( $element, attribute ) {
			if ( ! ( $element instanceof jQuery ) || typeof attribute !== 'string' ) {
				console.error( 'Invalid arguments passed to getDataAttributeAsInt' ); // eslint-disable-line no-console
				return NaN;
			}

			const selectedOption = $element.find( 'option:selected' );
			if ( selectedOption.length === 0 ) {
				console.error( 'No option selected in element', $element ); // eslint-disable-line no-console
				return NaN;
			}

			const value = selectedOption.data( attribute );
			const intValue = parseInt( value, 10 );

			if ( isNaN( intValue ) ) {
				console.error( `The data attribute "${ attribute }" is not a valid integer in element`, $element ); // eslint-disable-line no-console
				return NaN;
			}

			return intValue;
		}

		/**
		 * Formats a number as a currency string.
		 * @param {number} number - The number to format.
		 * @return {string} - The formatted currency string.
		 */
		formatCurrency( number ) {
			const numericValue = parseFloat( number );
			if ( isNaN( numericValue ) ) {
				console.error( 'Invalid number passed to formatCurrency:', number ); // eslint-disable-line no-console
				return '$0.00'; // or any other default/error value
			}

			return new Intl.NumberFormat( 'en-US', { style: 'currency', currency: 'USD' } ).format( numericValue );
		}
	}
	$( document ).on( 'gform_page_loaded, gform_post_render', function () {
		new TripPlanner();
		$( '#gform_3_validation_container' ).appendTo( '#gform_wrapper_3 div.gform_heading' ).first();
	} );
} )( jQuery );
